import React, { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export default function ContactForm({ isEn }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website_url: '' // Honeypot antispam felt
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      // Send som JSON i overensstemmelse med WEBFORM INTEGRATION reglerne
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject || (isEn ? 'Inquiry from website' : 'Henvendelse fra hjemmeside'),
          message: formData.message,
          website_url: formData.website_url // Honeypot skal medsendes
        })
      });

      if (!response.ok) {
        throw new Error(isEn ? 'Failed to submit form. Please try again.' : 'Kunne ikke indsende formularen. Prøv venligst igen.');
      }

      const result = await response.json();
      if (result.status === 'ok') {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          website_url: ''
        });
      } else {
        throw new Error(result.message || (isEn ? 'Validation failed.' : 'Validering fejlede.'));
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || (isEn ? 'An error occurred. Please try again.' : 'Der opstod en fejl. Prøv venligst igen.'));
    }
  };

  return (
    <div className="bg-brand-surface border border-gray-600 rounded-xl p-6 sm:p-8 shadow-xl relative">
      {status === 'success' ? (
        <div className="text-center py-12 space-y-4 animate-in fade-in duration-300">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 mb-2">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h3 className="font-display font-bold text-xl text-white">
            {isEn ? 'Thank you!' : 'Tak for din henvendelse!'}
          </h3>
          <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
            {isEn 
              ? 'We have received your message and will get back to you as soon as possible.'
              : 'Vi har modtaget din besked og vender tilbage til dig hurtigst muligt.'}
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-4 text-xs font-mono text-brand-accent hover:text-white transition-colors uppercase tracking-wider"
          >
            {isEn ? 'Send another message' : 'Send en ny besked'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input
              id="website_url"
              type="text"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Navn */}
            <div className="space-y-1.5">
              <label htmlFor="name" className="block text-xs font-mono uppercase tracking-wider text-gray-400">
                {isEn ? 'Your Name' : 'Dit navn'} <span className="text-brand-accent">*</span>
              </label>
              <input
                id="name"
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-gray-50 border border-gray-300 rounded px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent transition-colors"
                placeholder={isEn ? 'John Doe' : 'Dit fulde navn'}
              />
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-xs font-mono uppercase tracking-wider text-gray-400">
                {isEn ? 'Your Email' : 'Din e-mail'} <span className="text-brand-accent">*</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-gray-50 border border-gray-300 rounded px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent transition-colors"
                placeholder="name@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Telefonnummer */}
            <div className="space-y-1.5">
              <label htmlFor="phone" className="block text-xs font-mono uppercase tracking-wider text-gray-400">
                {isEn ? 'Phone Number' : 'Telefonnummer'}
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-gray-50 border border-gray-300 rounded px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent transition-colors"
                placeholder="+45 00 00 00 00"
              />
            </div>

            {/* Emne */}
            <div className="space-y-1.5">
              <label htmlFor="subject" className="block text-xs font-mono uppercase tracking-wider text-gray-400">
                {isEn ? 'Subject' : 'Emne'}
              </label>
              <input
                id="subject"
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full bg-gray-50 border border-gray-300 rounded px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent transition-colors"
                placeholder={isEn ? 'e.g., Marine Insulation' : 'f.eks. Marineisolering'}
              />
            </div>
          </div>

          {/* Besked */}
          <div className="space-y-1.5">
            <label htmlFor="message" className="block text-xs font-mono uppercase tracking-wider text-gray-400">
              {isEn ? 'Your Message' : 'Din besked'} <span className="text-brand-accent">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows="5"
              value={formData.message}
              onChange={handleChange}
              className="w-full bg-gray-50 border border-gray-300 rounded px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent transition-colors resize-none"
              placeholder={isEn ? 'Describe your project or insulation needs...' : 'Beskriv dit projekt eller dine isoleringsbehov...'}
            />
          </div>

          {/* Error Message */}
          {status === 'error' && (
            <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/20 rounded p-4 text-sm text-red-400 animate-in fade-in duration-200">
              <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full bg-brand-accent hover:bg-brand-accent-hover disabled:bg-brand-accent/50 text-white font-medium py-3.5 px-6 rounded transition-colors flex items-center justify-center gap-2 shadow-lg shadow-brand-accent/10"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isEn ? 'Sending...' : 'Sender...'}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                {isEn ? 'Send Message' : 'Send besked'}
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
